(() => {
  if (customElements.get('bloom-sticky-cart')) return;

  class BloomStickyCart extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-bloom-product-page]') || document.querySelector('[data-bloom-product-page]');
      this.form = document.getElementById(`AddToCartForm--${this.dataset.sectionId}`) || this.section?.querySelector('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
      this.mainButton = this.form?.querySelector('[data-add-to-cart]') || this.section?.querySelector('[data-add-to-cart]');
      if (!this.form || !this.mainButton) return;

      this.button = this.querySelector('[data-sticky-submit]');
      this.btnText = this.querySelector('[data-sticky-btn-text]');
      this.price = this.querySelector('[data-sticky-price]');
      this.comparePrice = this.querySelector('[data-sticky-compare-price]');
      this.select = this.querySelector('[data-sticky-variant-select]');
      this.status = this.querySelector('[data-sticky-status]');
      this.product = JSON.parse(this.section?.querySelector('[data-product-json]')?.textContent || '{"variants":[]}');

      this.abort = new AbortController();
      const signal = this.abort.signal;

      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.sync(); });
      };

      // Sync variant dropdown selection to main product form
      if (this.select) {
        this.select.addEventListener('change', () => {
          const variantId = this.select.value;
          const variant = this.product?.variants?.find((item) => String(item.id) === String(variantId));

          const selectedOption = this.select.options[this.select.selectedIndex];
          if (selectedOption) {
            const optPrice = selectedOption.dataset.price;
            const optCompare = selectedOption.dataset.comparePrice;
            if (optPrice && this.price) this.price.textContent = optPrice;
            if (this.comparePrice) {
              if (optCompare && optCompare !== '$0' && optCompare !== '$0.00' && optCompare !== '0' && optCompare !== '$ 0' && optCompare !== '$ 0.00') {
                this.comparePrice.textContent = optCompare;
                this.comparePrice.style.display = '';
              } else {
                this.comparePrice.textContent = '';
                this.comparePrice.style.display = 'none';
              }
            }
          }

          if (!variant) return;

          // 1. Update hidden product form select input
          const hiddenSelect = this.form.querySelector('[data-product-select], select[name="id"], input[name="id"]');
          if (hiddenSelect) {
            hiddenSelect.value = variant.id;
            hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }

          // 2. Click matching swatch/radio in main form if present
          const swatches = Array.from(this.form.querySelectorAll('[data-swatch-option], .swatch-element, input[type="radio"], .radio__button input'));
          swatches.forEach((swatch) => {
            const val = swatch.dataset?.value || swatch.value || swatch.getAttribute('data-value');
            if (val && (variant.options.includes(val) || variant.title.includes(val))) {
              if (typeof swatch.click === 'function') swatch.click();
              else swatch.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });

          // 3. Dispatch theme variant change
          document.dispatchEvent(new CustomEvent('theme:variant:change', {
            detail: { variant: variant, sectionId: this.dataset.sectionId },
            bubbles: true
          }));

          this.schedule();
        }, { signal });
      }

      this.section.addEventListener('theme:variant:change', this.schedule, { signal });
      this.form.addEventListener('change', this.schedule, { signal });
      this.form.addEventListener('input', this.schedule, { signal });
      window.addEventListener('scroll', this.schedule, { passive: true, signal });
      window.addEventListener('resize', this.schedule, { passive: true, signal });
      document.addEventListener('click', this.schedule, { signal });
      document.addEventListener('keydown', this.schedule, { signal });

      document.addEventListener('theme:scroll:lock', () => { this.locked = true; this.schedule(); }, { signal });
      document.addEventListener('theme:scroll:unlock', (event) => {
        clearTimeout(this.unlockTimer);
        this.unlockTimer = setTimeout(() => { this.locked = false; this.schedule(); }, Number(event.detail) || 0);
      }, { signal });

      this.button?.addEventListener('click', () => {
        this.sync();
        if (this.button.disabled) return;
        if (!this.form.reportValidity()) {
          this.form.scrollIntoView({ block: 'center', behavior: 'smooth' });
          return;
        }
        // Trigger native Add to Cart
        this.mainButton.click();
        this.schedule();
      }, { signal });

      this.observer = new MutationObserver(this.schedule);
      this.observer.observe(this.form, { subtree: true, childList: true, characterData: true, attributes: true });

      const priceSource = this.section.querySelector('[data-price-wrapper]');
      if (priceSource) this.observer.observe(priceSource, { subtree: true, childList: true, characterData: true, attributes: true });

      this.observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      this.observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

      this.resizeObserver = new ResizeObserver(this.schedule);
      this.resizeObserver.observe(this);

      document.body.classList.add('bloom-sticky-cart-enabled');
      this.sync();
    }

    sync() {
      const variantId = new FormData(this.form).get('id') || this.form.querySelector('[data-product-select]')?.value;
      const variant = this.product.variants.find((item) => String(item.id) === String(variantId)) || this.product.variants[0];

      // Sync dropdown value
      if (this.select && variant && String(this.select.value) !== String(variant.id)) {
        this.select.value = variant.id;
      }

      // Sync button state
      const busy = this.mainButton.classList.contains('is-loading') || this.mainButton.getAttribute('aria-busy') === 'true';
      const available = variant ? variant.available : true;
      const disabled = !available || this.mainButton.disabled || busy;
      if (this.button) {
        this.button.disabled = disabled;
        this.button.setAttribute('aria-busy', String(busy));
        const label = !available ? this.dataset.soldOutLabel : (this.mainButton.querySelector('[data-add-to-cart-text]')?.textContent.trim() || this.dataset.addToCartLabel || 'Add to Cart');
        if (this.btnText) this.btnText.textContent = label;
        else if (this.button.textContent !== label) this.button.textContent = label;
      }

      // Sync price
      const priceSource = this.section.querySelector('[data-product-price]')?.textContent.trim();
      if (priceSource && this.price) {
        this.price.textContent = priceSource;
      }

      // Sync compare price
      const comparePriceEl = this.section.querySelector('[data-compare-price]');
      const comparePriceSource = comparePriceEl?.textContent.trim() || '';
      const isComparePriceHidden = !comparePriceSource ||
        comparePriceSource === '' ||
        comparePriceSource === '$0' ||
        comparePriceSource === '$0.00' ||
        comparePriceSource === '0' ||
        comparePriceSource === '$ 0' ||
        comparePriceSource === '$ 0.00' ||
        (comparePriceEl && (comparePriceEl.classList.contains('hide') || comparePriceEl.style.display === 'none' || comparePriceEl.getAttribute('aria-hidden') === 'true' || comparePriceEl.offsetParent === null));

      if (this.comparePrice) {
        if (!isComparePriceHidden) {
          this.comparePrice.textContent = comparePriceSource;
          this.comparePrice.style.display = '';
        } else {
          this.comparePrice.textContent = '';
          this.comparePrice.style.display = 'none';
        }
      }

      // Sync errors
      const errors = this.form.querySelector('[data-cart-errors-container]')?.textContent.trim() || '';
      if (this.status && this.status.textContent !== errors) {
        this.status.textContent = errors;
        if (errors) this.form.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }

      // Modal check
      const visibleModal = [...document.querySelectorAll('dialog[open], .pswp--open, [aria-modal="true"]:not([aria-hidden="true"])')].some((modal) => {
        if (modal.checkVisibility) {
          if (!modal.checkVisibility({ visibilityProperty: true, opacityProperty: true })) return false;
        } else {
          for (let ancestor = modal; ancestor; ancestor = ancestor.parentElement) {
            const style = getComputedStyle(ancestor);
            if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
          }
        }
        const rect = modal.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
      });

      // Visibility trigger: show when scrolled past the main button or scrolled down 280px+
      const btnRect = this.mainButton.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const passedButton = btnRect.bottom < 120 || (scrollY > 320 && btnRect.top < 80);

      this.hidden = !passedButton || Boolean(visibleModal);
      if (!this.hidden) {
        const height = this.offsetHeight || 110;
        document.body.style.setProperty('--bloom-sticky-space', `${height}px`);
        document.body.classList.add('bloom-sticky-cart-visible');
      } else {
        document.body.classList.remove('bloom-sticky-cart-visible');
      }
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.unlockTimer);
      document.body.classList.remove('bloom-sticky-cart-enabled');
      document.body.classList.remove('bloom-sticky-cart-visible');
      document.body.style.removeProperty('--bloom-sticky-space');
    }
  }
  customElements.define('bloom-sticky-cart', BloomStickyCart);
})();

// Bloom Custom Nails Concierge Modal & Smart Mail Handler
(() => {
  const initCustomNails = () => {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-bloom-nails-action]');
      if (!trigger) return;

      const title = trigger.dataset.title || 'Custom Nails Request';
      const email = trigger.dataset.email || 'Hello@BloomNails.com';
      const gmailUrl = trigger.dataset.gmailUrl || '';
      const mailtoUrl = trigger.getAttribute('href') || `mailto:${email}`;
      const contactUrl = trigger.dataset.contactUrl || '/pages/contact';
      const isCustom = trigger.dataset.type === 'custom';

      // If custom URL is set and not mailto, let normal navigation occur
      if (trigger.getAttribute('href') && !trigger.getAttribute('href').startsWith('mailto:')) {
        return;
      }

      e.preventDefault();

      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth <= 767;

      let modal = document.getElementById('BloomNailsModal');
      if (!modal) {
        modal = document.createElement('div');
        modal.id = 'BloomNailsModal';
        modal.className = 'bloom-nails-modal';
        modal.innerHTML = `
          <div class="bloom-nails-modal__box" role="dialog" aria-modal="true">
            <button type="button" class="bloom-nails-modal__close" aria-label="Close modal">&times;</button>
            <div class="bloom-nails-modal__header">
              <div class="bloom-nails-modal__icon" id="BloomModalIcon"></div>
              <div>
                <h3 class="bloom-nails-modal__title" id="BloomModalTitle"></h3>
                <span style="font-size: 13px; color: #aa7526; font-weight: 600;">Bloom Nails Concierge</span>
              </div>
            </div>
            <p class="bloom-nails-modal__desc">
              Send your request directly to our nail specialists at <strong>${email}</strong>:
            </p>
            <div class="bloom-nails-modal__actions">
              <a href="#" class="bloom-nails-modal__btn bloom-nails-modal__btn--primary" id="BloomModalGmail" target="_blank" rel="noopener">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                <span>Open in Gmail (Web)</span>
              </a>
              <a href="#" class="bloom-nails-modal__btn bloom-nails-modal__btn--secondary" id="BloomModalMailto">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.2 8.4c.5.38.8.97.8 1.6v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V10a2 2 0 0 1 .8-1.6l8-6a2 2 0 0 1 2.4 0l8 6z"></path><polyline points="22 10 12 16 2 10"></polyline></svg>
                <span>Open Default Mail App</span>
              </a>
              <button type="button" class="bloom-nails-modal__btn bloom-nails-modal__btn--outline" id="BloomModalCopy">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                <span id="BloomCopyText">Copy Email (${email})</span>
              </button>
              <a href="${contactUrl}" class="bloom-nails-modal__btn bloom-nails-modal__btn--link" style="font-size: 13px; color: #6d5845; text-decoration: underline !important; padding: 6px; text-align: center;">
                Or fill out our Contact Page Form &rarr;
              </a>
              <div class="bloom-nails-modal__toast" id="BloomToast">✓ Email address copied to clipboard!</div>
            </div>
          </div>
        `;
        document.body.appendChild(modal);

        const closeModal = () => modal.classList.remove('is-active');
        modal.querySelector('.bloom-nails-modal__close').addEventListener('click', closeModal);
        modal.addEventListener('click', (ev) => {
          if (ev.target === modal) closeModal();
        });
        document.addEventListener('keydown', (ev) => {
          if (ev.key === 'Escape' && modal.classList.contains('is-active')) closeModal();
        });

        const copyBtn = modal.querySelector('#BloomModalCopy');
        copyBtn.addEventListener('click', () => {
          navigator.clipboard.writeText(email).then(() => {
            const toast = modal.querySelector('#BloomToast');
            const copyText = modal.querySelector('#BloomCopyText');
            toast.classList.add('is-shown');
            copyText.textContent = '✓ Copied!';
            setTimeout(() => {
              toast.classList.remove('is-shown');
              copyText.textContent = `Copy Email (${email})`;
            }, 2500);
          }).catch(() => {
            window.prompt('Copy email address:', email);
          });
        });
      }

      modal.querySelector('#BloomModalTitle').textContent = title;
      modal.querySelector('#BloomModalGmail').setAttribute('href', gmailUrl);
      modal.querySelector('#BloomModalMailto').setAttribute('href', mailtoUrl);

      const iconWrap = modal.querySelector('#BloomModalIcon');
      if (isCustom) {
        iconWrap.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l2.4 6.6L21 11l-6.6 2.4L12 20l-2.4-6.6L3 11l6.6-2.4L12 2z" fill="currentColor" fill-opacity="0.18"></path><path d="M19 2l.8 2.2L22 5l-2.2.8L19 8l-.8-2.2L16 5l2.2-.8L19 2z" fill="currentColor" fill-opacity="0.25"></path></svg>';
      } else {
        iconWrap.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M7 21h7a3 3 0 0 0 3-3V8.5a6.5 6.5 0 0 0-13 0V18a3 3 0 0 0 3 3z" fill="currentColor" fill-opacity="0.18"></path><path d="M17.5 3v6M14.5 6h6" stroke-width="2"></path></svg>';
      }

      modal.classList.add('is-active');

      if (isMobile) {
        window.location.href = mailtoUrl;
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomNails);
  } else {
    initCustomNails();
  }
})();

// Product Gallery Slider & Thumbnails Synchronizer
// Oct 2026 fix: never scrolls the page itself (iOS fires "resize" when the address bar hides,
// which used to call scrollIntoView on the first thumbnail and throw shoppers back to the top).
(() => {
  const centerThumb = (container, thumb) => {
    if (!container || !thumb || window.innerWidth > 767) return;
    if (container.scrollWidth <= container.clientWidth) return;
    const c = container.getBoundingClientRect();
    const t = thumb.getBoundingClientRect();
    const left = container.scrollLeft + (t.left - c.left) - (c.width - t.width) / 2;
    container.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  };

  const initGallerySync = () => {
    const gallery = document.querySelector('[data-product-single-media-group]') || document.querySelector('.product-single__gallery');
    if (!gallery) return;

    const slider = gallery.querySelector('[data-product-single-media-slider], .product-single__media-slider');
    const thumbsContainer = gallery.querySelector('[data-product-single-media-thumbs], .product-single__thumbnails');
    if (!slider) return;

    const flkty = window.Flickity && window.Flickity.data(slider);
    if (flkty) {
      flkty.resize();
      flkty.reposition();
    }

    if (!thumbsContainer) return;
    const thumbs = Array.from(thumbsContainer.querySelectorAll('.product-single__thumbnail'));
    const slides = Array.from(slider.querySelectorAll('.product-single__media-slide'));
    if (thumbs.length <= 1) return;

    const updateActiveThumb = (idx, scroll) => {
      thumbs.forEach((t, i) => {
        const isCurrent = i === idx;
        t.classList.toggle('is-active', isCurrent);
        t.classList.toggle('is-nav-selected', isCurrent);
        t.classList.toggle('is-selected', isCurrent);
      });
      if (scroll) centerThumb(thumbsContainer, thumbs[idx]);
    };

    if (!thumbsContainer.dataset.bloomSynced) {
      thumbsContainer.dataset.bloomSynced = '1';
      thumbs.forEach((thumb, idx) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          const currentFlkty = window.Flickity && window.Flickity.data(slider);
          if (currentFlkty) {
            currentFlkty.select(idx);
          } else {
            slides.forEach((s, i) => { s.style.display = i === idx ? 'block' : 'none'; });
          }
          updateActiveThumb(idx, true);
        });
      });
    }

    if (flkty && !slider.dataset.bloomSelectBound) {
      slider.dataset.bloomSelectBound = '1';
      flkty.on('select', () => { updateActiveThumb(flkty.selectedIndex, true); });
    }

    updateActiveThumb(flkty ? flkty.selectedIndex : 0, false);
  };

  const syncProductTitleAlignment = () => {
    if (window.innerWidth < 768) return;
    const breadcrumbs = document.querySelector('[data-bloom-product-page] .bloom-gallery-breadcrumbs');
    const details = document.querySelector('[data-bloom-product-page] .product-single__details');
    if (breadcrumbs && details) {
      const rect = breadcrumbs.getBoundingClientRect();
      const style = window.getComputedStyle(breadcrumbs);
      const mb = parseFloat(style.marginBottom) || 12;
      const total = rect.height + mb;
      if (total > 0) {
        const pt = Math.max(0, Math.round(total - 5));
        details.style.setProperty('--bloom-details-pt', `${pt}px`);
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(initGallerySync, 100);
      syncProductTitleAlignment();
    });
  } else {
    setTimeout(initGallerySync, 100);
    syncProductTitleAlignment();
  }
  window.addEventListener('load', () => {
    initGallerySync();
    syncProductTitleAlignment();
  });
  // Only react to real width changes (rotation, desktop resize), not the mobile address bar showing/hiding.
  let lastWidth = window.innerWidth;
  window.addEventListener('resize', () => {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    requestAnimationFrame(initGallerySync);
    syncProductTitleAlignment();
  }, { passive: true });
  document.addEventListener('theme:variant:change', () => {
    setTimeout(initGallerySync, 100);
    syncProductTitleAlignment();
  });
})();
